// iter498 — Smart-Group Rules library panel.
// Mounted inside BankingRulesPage to give a single home for ALL banking
// rules (existing description-based party-link rules + the new
// Smart-Group → Expense rules introduced in iter497). Persistent rules
// never auto-fire — this panel only edits / pauses / deletes them. The
// actual "Apply" button lives inside each bank-statement's Smart Groups
// tab so the user is always explicit about which statement gets touched.
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Pause, Play, Trash2, RefreshCw, BookmarkPlus, Edit3 } from 'lucide-react';

const fmtDate = (s) => (s || '').slice(0, 10);

export function SmartGroupRulesPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [editing, setEditing] = useState(null); // {id, name, category}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/banking/group-rules');
      setRules(r.data?.rules || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    setBusy(b => ({ ...b, [r.id]: true }));
    try {
      await api.patch(`/banking/group-rules/${r.id}`, { active: !r.active });
      toast.success(`Rule ${!r.active ? 'enabled' : 'paused'}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusy(b => ({ ...b, [r.id]: false }));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"? Past applications are NOT undone — only future runs are removed.`)) return;
    try {
      await api.delete(`/banking/group-rules/${r.id}`);
      toast.success('Rule deleted.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    try {
      await api.patch(`/banking/group-rules/${editing.id}`, {
        name: editing.name.trim() || undefined,
        category: editing.category.trim().toLowerCase() || undefined,
      });
      toast.success('Rule updated.');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="border border-violet-200 bg-violet-50/60 rounded-xl p-4" data-testid="smart-group-rules-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <BookmarkPlus size={18} className="text-violet-700" />
          <div>
            <h2 className="font-semibold text-base">Smart-Group Rules</h2>
            <p className="text-[11px] text-muted-foreground">
              Auto-expense rules for bank-statement groups (POS fees, VAT, SADAD, etc.). <strong>Never auto-fire</strong> — apply from the Smart Groups tab on any statement.
            </p>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="h-7 text-xs" data-testid="sgr-refresh">
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-muted-foreground py-3 italic">
          No Smart-Group rules yet. Open any bank statement → Smart Groups tab → click "Add to Expense" and choose "Save as rule".
        </div>
      ) : (
        <div className="space-y-1">
          {rules.map(r => (
            <div key={r.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded bg-white/80 border border-white/40" data-testid={`sgr-row-${r.id}`}>
              <span className={`shrink-0 w-2 h-2 rounded-full ${r.active ? 'bg-emerald-500' : 'bg-slate-300'}`} title={r.active ? 'Active' : 'Paused'} />
              {editing?.id === r.id ? (
                <>
                  <Input
                    className="h-6 text-[11px] flex-1 min-w-[180px]"
                    value={editing.name}
                    onChange={(e) => setEditing(s => ({ ...s, name: e.target.value }))}
                    autoFocus
                    placeholder="Rule name"
                  />
                  <Input
                    className="h-6 text-[11px] w-[140px]"
                    value={editing.category}
                    onChange={(e) => setEditing(s => ({ ...s, category: e.target.value }))}
                    placeholder="category"
                  />
                  <Button size="sm" className="h-6 text-[10px] px-2 bg-violet-600 hover:bg-violet-700 text-white" onClick={saveEdit}>Save</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => setEditing(null)}>Cancel</Button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="font-medium flex-1 text-left truncate hover:text-violet-700"
                    onClick={() => setEditing({ id: r.id, name: r.name || '', category: r.category || '' })}
                    title="Click to edit"
                    data-testid={`sgr-edit-${r.id}`}
                  >
                    {r.name}
                  </button>
                  <Badge variant="outline" className="text-[10px]">{r.group_key}</Badge>
                  <Badge variant="outline" className="text-[10px]">→ {r.category}</Badge>
                  {r.run_count > 0 && (
                    <span className="text-[10px] text-muted-foreground" title={`Last run: ${fmtDate(r.last_run_at) || '—'}`}>
                      {r.run_count}× run · last {fmtDate(r.last_run_at) || '—'}
                    </span>
                  )}
                  <button type="button" className="p-1 text-violet-600 hover:text-violet-800" onClick={() => setEditing({ id: r.id, name: r.name || '', category: r.category || '' })} title="Edit" data-testid={`sgr-edit-btn-${r.id}`}>
                    <Edit3 size={11} />
                  </button>
                  <button
                    type="button"
                    className="p-1 text-slate-600 hover:text-slate-900"
                    onClick={() => toggle(r)}
                    disabled={!!busy[r.id]}
                    title={r.active ? 'Pause' : 'Resume'}
                    data-testid={`sgr-toggle-${r.id}`}
                  >
                    {busy[r.id] ? <Loader2 size={11} className="animate-spin" /> : (r.active ? <Pause size={11} /> : <Play size={11} />)}
                  </button>
                  <button
                    type="button"
                    className="p-1 text-rose-600 hover:text-rose-800"
                    onClick={() => remove(r)}
                    title="Delete"
                    data-testid={`sgr-delete-${r.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SmartGroupRulesPanel;
